// A wine hue as a glossy swatch with its name written under it, a toggle in a group of hues. The
// chosen one is marked with a ring and a check, never colour alone; the name is the control's label.
import "./config";
import { View, styled } from "tamagui";
import { Svg, paint } from "./Graphic";
import { Pressable } from "./controls";
import type { PaletteKey } from "./palette";
import { useSvgId } from "./svgId";
import { Caption } from "./text";

const SwatchFrame = styled(Pressable, {
  name: "HueSwatch",
  gap: "1",
  minWidth: "var(--uiTarget)",
  minHeight: "var(--uiTarget)",
  paddingVertical: "1",
  paddingHorizontal: "1",
  borderRadius: "2",
});

/** The ring is always laid out (transparent when unchosen), so choosing a hue moves nothing. */
const Ring = styled(View, {
  name: "HueSwatchRing",
  padding: 2,
  borderRadius: 14,
  borderWidth: { default: 3, a11yLight: 4, a11yDark: 4 },
  borderColor: "transparent",
  variants: { chosen: { true: { borderColor: "accent" } } } as const,
});

export interface HueSwatchProps {
  /** The hue's palette token (`hueRuby`, `wineRed`, ...). */
  tone: PaletteKey;
  /** The hue's name, written under the swatch and read out as the button's name. */
  name: string;
  selected: boolean;
  onPress: () => void;
  /** The swatch's side in px. */
  size?: number;
}

export function HueSwatch({
  tone,
  name,
  selected,
  onPress,
  size = 40,
}: HueSwatchProps): React.JSX.Element {
  const id = useSvgId();
  return (
    <SwatchFrame aria-pressed={selected} onPress={onPress}>
      <Ring chosen={selected}>
        <Svg viewBox="0 0 40 40" width={size} height={size}>
          <defs>
            <linearGradient id={`${id}-gloss`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff" stopOpacity=".45" />
              <stop offset=".45" stopColor="#fff" stopOpacity="0" />
              <stop offset="1" stopColor="#000" stopOpacity=".2" />
            </linearGradient>
          </defs>
          <rect data-part="hue" width="40" height="40" rx="10" fill={paint(tone)} />
          <rect data-part="gloss" width="40" height="40" rx="10" fill={`url(#${id}-gloss)`} />
          <rect
            width="40"
            height="40"
            rx="10"
            fill="none"
            stroke={paint("lineStrong")}
            strokeWidth="var(--borderW)"
          />
          {selected ? (
            <g data-part="check" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.5 20.5l6 6 11-13" stroke="#000" strokeOpacity=".45" strokeWidth="5.6" />
              <path d="M11.5 20.5l6 6 11-13" stroke="#fff" strokeWidth="3.4" />
            </g>
          ) : null}
        </Svg>
      </Ring>
      <Caption color={selected ? "text" : "textDim"} fontWeight={selected ? "700" : "500"}>
        {name}
      </Caption>
    </SwatchFrame>
  );
}
